import { requiredFieldsOf } from '@atlas/core-utils/work-order'
import type { RequiredWorkOrderField } from '@atlas/types'
import { ExternalSection } from '@/components/work-orders/ExternalSection'
import { isFromWorkfront } from '@/components/work-orders/WorkfrontBadges'
import type { WorkOrder } from '@/domain/work-order.types'
import { HookCode, type ResolvedWorkflowSnapshot } from '@/domain/workflow.types'
import { useAssignPm } from '@/hooks/useAssignDesigner'
import { useUserNames } from '@/hooks/useUserNames'
import { useActivePms, useUsers } from '@/hooks/useUsers'
import { activePms } from '@/lib/option-order'
import { WorkOrderRefreshFromWorkfront } from '../WorkOrders/WorkOrderWorkfrontHeader'
import { AssignDesignerCard } from './AssignDesignerCard'
import type { StageMarket } from './StageHeader'
import { designerOptions, requiredFieldRows } from './stage-helpers'
import { VerifyRequestCard } from './VerifyRequestCard'

const PM_SLOT = { title: 'Assign PM', label: 'PM', placeholder: 'Choose a PM' }

/** The fields the move out of the order's current status requires, from its `fields_present` guards. */
const requiredFieldsToLeave = (snapshot: ResolvedWorkflowSnapshot | null | undefined): RequiredWorkOrderField[] => [
	...new Set(
		(snapshot?.legalTransitions ?? [])
			.flatMap((t) => t.guards)
			.filter((g) => g.hookCode === HookCode.FIELDS_PRESENT)
			.flatMap((g) => requiredFieldsOf(g.params ?? {})),
	),
]

function PmPicker({ workOrder }: { workOrder: WorkOrder }) {
	const { data: users } = useUsers()
	const { data: pms } = useActivePms()
	const userNames = useUserNames()
	const assignPm = useAssignPm(workOrder)
	return (
		<AssignDesignerCard
			slot={PM_SLOT}
			options={designerOptions(activePms(pms, users, workOrder.pmUserId))}
			value={workOrder.pmUserId}
			onChange={(userId) => assignPm(userId, userNames.get(userId))}
		/>
	)
}

/**
 * The Created step: the PM checks the request carries everything production needs. A synced
 * order's values are Workfront's, so they are fixed there and refreshed here, never edited.
 */
export function CreatedStep({
	workOrder,
	snapshot,
	brand,
	market,
	canPickPm,
}: {
	workOrder: WorkOrder
	snapshot: ResolvedWorkflowSnapshot | null | undefined
	brand?: string | null
	market: StageMarket | null
	canPickPm: boolean
}) {
	const rows = requiredFieldRows(workOrder, requiredFieldsToLeave(snapshot), { brand: brand ?? null, market })
	const workfront = isFromWorkfront(workOrder) ? (
		<>
			<ExternalSection order={workOrder} />
			<div className="mt-4">
				<WorkOrderRefreshFromWorkfront workOrderId={workOrder.workOrderId} />
			</div>
		</>
	) : undefined
	return (
		<>
			{canPickPm ? <PmPicker workOrder={workOrder} /> : null}
			<VerifyRequestCard
				rows={rows}
				workfront={workfront}
				editHref={workfront ? undefined : `/work-orders/${workOrder.workOrderId}/edit`}
			/>
		</>
	)
}
